"use client"
import React, { useEffect, useRef } from "react"

const NAV_SEARCH_PARAM = "search"
const RAINFOCUS_SEARCH_PARAM = "search-rainfocus"
const RESTORE_TIMEOUT_MS = 15_000

export interface RainfocusWidgetProps {
  apiToken: string
  widgetId: string
  env?: "prod" | "staging" | "dev"
}

declare global {
  interface Window {
    jQuery?: any
    Rainfocus?: {
      Widget: new (config: {
        apiToken: string
        widgetId: string
        env: string
      }) => any
    }
    widget?: any
  }
}

/** Bypass Next/our history wrappers so temporary URL swaps don't notify React. */
const nativeReplaceUrl = (url: string) => {
  History.prototype.replaceState.call(
    window.history,
    window.history.state,
    "",
    url,
  )
}

const buildUrlWithSearchParam = (
  fromParam: string,
  toParam: string,
  term: string,
) => {
  const next = new URL(window.location.href)
  next.searchParams.delete(fromParam)
  next.searchParams.delete(toParam)
  next.searchParams.set(toParam, term)
  // Rainfocus parses the query string itself and treats "+" as a literal
  // character, so keep spaces as %20 instead of URLSearchParams' "+".
  const query = next.searchParams.toString().replace(/\+/g, "%20")
  return `${next.pathname}${query ? `?${query}` : ""}${next.hash}`
}

const normalizeSearchTerm = (term: string) =>
  term.trim().toLowerCase().replace(/\+/g, " ")

const catalogShowsSearchTerm = (root: HTMLElement, term: string) => {
  const normalized = normalizeSearchTerm(term)
  if (!normalized) return false
  const text = normalizeSearchTerm(root.innerText)
  return text.includes(`"${normalized}"`) || text.includes(`“${normalized}”`)
}

/**
 * Rainfocus only restores filters from its native `?search=` param. Shared
 * links use `?search-rainfocus=` (to avoid colliding with nav Algolia). Briefly
 * present the pasted term as `search` (via prototype replaceState so Next/nav
 * never see it), then restore `search-rainfocus` once the catalog has applied
 * the filter, on timeout, or on unmount.
 */
const exposeNativeSearchParam = (root: HTMLElement, signal: AbortSignal) => {
  const params = new URLSearchParams(window.location.search)
  const term = params.get(RAINFOCUS_SEARCH_PARAM)
  // Skip when empty, or when nav already owns `search` on the same URL.
  if (term === null || term === "" || params.has(NAV_SEARCH_PARAM)) return

  let restored = false
  const teardowns: Array<() => void> = []

  const restore = () => {
    if (restored) return
    restored = true
    teardowns.forEach((teardown) => teardown())

    const current = new URLSearchParams(window.location.search)
    if (
      current.has(NAV_SEARCH_PARAM) &&
      !current.has(RAINFOCUS_SEARCH_PARAM)
    ) {
      nativeReplaceUrl(
        buildUrlWithSearchParam(NAV_SEARCH_PARAM, RAINFOCUS_SEARCH_PARAM, term),
      )
    }
  }

  nativeReplaceUrl(
    buildUrlWithSearchParam(RAINFOCUS_SEARCH_PARAM, NAV_SEARCH_PARAM, term),
  )

  const observer = new MutationObserver(() => {
    if (catalogShowsSearchTerm(root, term)) restore()
  })
  observer.observe(root, {
    childList: true,
    subtree: true,
    characterData: true,
  })
  teardowns.push(() => observer.disconnect())

  const timeoutId = setTimeout(restore, RESTORE_TIMEOUT_MS)
  teardowns.push(() => clearTimeout(timeoutId))

  signal.addEventListener("abort", restore, { once: true })

  // Chip may already be present if the catalog restored instantly.
  if (catalogShowsSearchTerm(root, term)) restore()
}

export const RainfocusWidget: React.FC<RainfocusWidgetProps> = ({
  apiToken,
  widgetId,
  env = "prod",
}) => {
  const widgetRef = useRef<HTMLDivElement>(null)
  const scriptsLoadedRef = useRef(false)

  // The catalog syncs its filter term to `?search=`, the same param the site
  // nav deep-links its Algolia search with, so typing here used to open the
  // nav's search overlay. Rename the catalog's writes to keep them separate.
  useEffect(() => {
    // Scoped to the bundle's own calls so the nav keeps deep-linking `search`.
    const fromRainfocus = () => (new Error().stack ?? "").includes("rainfocus")

    const rename = (url: Parameters<History["pushState"]>[2]) => {
      if (url == null || !fromRainfocus()) return url
      const next = new URL(String(url), window.location.href)
      const term = next.searchParams.get(NAV_SEARCH_PARAM)
      if (term === null) return url
      next.searchParams.delete(NAV_SEARCH_PARAM)
      next.searchParams.set(RAINFOCUS_SEARCH_PARAM, term)
      return `${next.pathname}${next.search}${next.hash}`
    }

    const { pushState, replaceState } = window.history
    window.history.pushState = function (state, title, url) {
      return pushState.call(this, state, title, rename(url))
    }
    window.history.replaceState = function (state, title, url) {
      return replaceState.call(this, state, title, rename(url))
    }
    return () => {
      window.history.pushState = pushState
      window.history.replaceState = replaceState
    }
  }, [])

  useEffect(() => {
    const hydrateAbort = new AbortController()

    const loadRainfocusResources = async () => {
      if (scriptsLoadedRef.current) return

      try {
        // Load jQuery/ CSS Files // Rainfocus Bundle
        if (!window.jQuery) {
          await loadScript(
            "https://ajax.googleapis.com/ajax/libs/jquery/3.6.1/jquery.min.js",
          )
        }

        await loadCSS(
          "https://static.rainfocus.com/gfonts/css/e47fce46a9d2402094879d2f861fb21e.css",
        )
        await loadCSS(
          "https://events.rainfocus.com/scripts/catalog/bundle.min.css",
        )
        await loadCSS(
          "https://events.rainfocus.com/assets/css/rainfocusWidgets.css",
        )

        await loadScript("https://events.rainfocus.com/js/bundle.js")

        if (hydrateAbort.signal.aborted) return
        scriptsLoadedRef.current = true
        initializeWidget()
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        // console.error('Failed to load Rainfocus resources:', error)
      }
    }

    const loadScript = (src: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve()
          return
        }

        const script = document.createElement("script")
        script.src = src
        script.onload = () => resolve()
        script.onerror = () =>
          reject(new Error(`Failed to load script: ${src}`))
        document.head.appendChild(script)
      })
    }

    const loadCSS = (href: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`link[href="${href}"]`)) {
          resolve()
          return
        }

        const link = document.createElement("link")
        link.rel = "stylesheet"
        link.href = href
        link.onload = () => resolve()
        link.onerror = () => reject(new Error(`Failed to load CSS: ${href}`))
        document.head.appendChild(link)
      })
    }

    const initializeWidget = () => {
      const Rainfocus = window.Rainfocus
      const root = widgetRef.current
      if (Rainfocus && root) {
        try {
          exposeNativeSearchParam(root, hydrateAbort.signal)
          window.widget = new Rainfocus.Widget({
            apiToken,
            widgetId,
            env,
          })
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          // console.error('Failed to initialize Rainfocus widget:', error)
        }
      }
    }

    loadRainfocusResources()

    // Cleanup function
    return () => {
      hydrateAbort.abort()
      if (window.widget) {
        // Clean up widget if needed
        window.widget = undefined
      }
    }
  }, [apiToken, widgetId, env])

  return <div ref={widgetRef} className="rf-widget" id="rf-catalog" />
}
